import React, { memo, useEffect, useState } from 'react';
import { FlatList, Platform, useWindowDimensions, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import * as S from './style';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import { borderRadiusButtons } from '../../config/data';
import THEME from '../../config/theme';

// 0.7rem, o mesmo padding lateral do `HeaderContainer`.
const HEADER_GUTTER = 11;

export const FilterBar = memo(
  ({
    filters,
    selected,
    onSelect,
    colors,
    fonts,
    showClear,
    onReorder,
    contentPaddingHorizontal,
    extraMenuActions = [],
  }) => {
    const { customInfo, customColor, customFont } = useCustomTheme();

    // ── Escala dos chips ──────────────────────────────────────────────────────
    // O tamanho vinha de `customFont.fontMD` (18px por padrão), pensado para
    // texto corrido: num chip, no celular, isso vira um badge enorme. Aqui o
    // corpo do texto é limitado; no desktop segue a fonte da marca.
    const { width } = useWindowDimensions();
    const isSmallScreen = width < 768;
    const baseFontSize = customFont?.fontMD || THEME?.FONTSIZE?.MEDIUM;
    const chipFontSize = isSmallScreen
      ? Math.min(baseFontSize, 13)
      : baseFontSize;

    // ── Reordenação dos filtros por drag and drop (admin, apenas web) ────────
    // Mesmo padrão dos cards da categoria e dos conteúdos do módulo: HTML5 DnD
    // num wrapper <div> cru, seguro porque o react-native-web renderiza em DOM.
    // No app NATIVO o wrapper não é aplicado (um <div> quebraria o render) e os
    // chips seguem estáticos — lá a ordem é definida pelo campo "Ordem" no
    // modal de filtros. `onReorder` só chega quando o usuário é admin.
    const canDrag = !!onReorder && Platform.OS === 'web' && filters.length > 1;
    const [localFilters, setLocalFilters] = useState(null);
    const [dragState, setDragState] = useState({ from: null, over: null });

    // Ordem otimista local vale até o pai reenviar os filtros já persistidos.
    useEffect(() => {
      setLocalFilters(null);
    }, [filters]);

    const visibleFilters = (localFilters || filters).filter((f) => f && f.value);

    const handleDrop = (from, to) => {
      setDragState({ from: null, over: null });
      if (from == null || to == null || from === to) return;
      const reordered = [...visibleFilters];
      const [moved] = reordered.splice(from, 1);
      reordered.splice(to, 0, moved);
      setLocalFilters(reordered); // feedback imediato
      Promise.resolve(onReorder(reordered)).catch((error) => {
        console.error('Falha ao salvar a nova ordem dos filtros:', error);
        setLocalFilters(null); // desfaz o otimismo: volta à ordem persistida
        Toast.show({
          type: 'error',
          text1: 'Não foi possível salvar a nova ordem dos filtros.',
          text2: 'Tente novamente mais tarde',
        });
      });
    };

    const data = [
      ...(selected && showClear ? [{ type: 'clear' }] : []),
      ...visibleFilters.map((f, index) => ({ type: 'filter', item: f, index })),
      ...extraMenuActions.map((action) => ({
        type: 'action',
        key: `action:${action.label}`,
        label: action.label,
        onPress: action.onPress,
      })),
    ];

    const renderRow = ({ item }) => {
      if (item.type === 'clear') {
        return (
          <S.ClearButton
            compact={isSmallScreen}
            borderRadiusButton={
              customInfo && customInfo.borderRadius
              ? customInfo.borderRadius
              : borderRadiusButtons
            }
            onPress={() => onSelect(null)}
            borderColor={`${colors}90`}
          >
            <Ionicons name="close" size={chipFontSize + 3} color={colors} />
          </S.ClearButton>
        );
      }

      if (item.type === 'action') {
        return (
          <S.Chip
            compact={isSmallScreen}
            borderRadiusButton={
              customInfo && customInfo?.borderRadius
                ? customInfo.borderRadius
                : borderRadiusButtons
            }
            onPress={item.onPress}
            borderColor={`${colors}90`}
            backgroundColor="transparent"
          >
            <S.Text
              fontWeight="normal"
              fontFamily={fonts}
              color={colors}
              fontSize={chipFontSize}
            >
              {item.label}
            </S.Text>
          </S.Chip>
        );
      }

      const active = item.item.value === selected;

      const chip = (
        <S.Chip
          compact={isSmallScreen}
          borderRadiusButton={
            customInfo && customInfo?.borderRadius
              ? customInfo.borderRadius
              : borderRadiusButtons
          }
          onPress={() => onSelect(item.item.value)}
          borderColor={active ? (customColor &&
            customColor?.button &&
            customColor?.button?.backgroundApp
            ? customColor.button.backgroundApp
            : THEME?.COLORS?.PRIMARY_900) : `${colors}90`}
          backgroundColor={active ? (customColor &&
            customColor?.button &&
            customColor?.button?.backgroundApp
            ? customColor.button.backgroundApp
            : THEME?.COLORS?.PRIMARY_900) : 'transparent'}
        >
          <S.Text
            fontWeight={active ? 'bold' : 'normal'}
            fontFamily={fonts}
            color={active ? (customColor && customColor.button && customColor.button.textApp
              ? customColor.button.textApp
              : THEME?.COLORS?.TEXT_BUTTON) : colors}
            fontSize={chipFontSize}
          >
            {item.item.label}
          </S.Text>
        </S.Chip>
      );

      if (!canDrag) return chip;

      const index = item.index;
      const isDragging = dragState.from === index;
      const isDropTarget =
        dragState.from !== null && dragState.over === index && dragState.from !== index;
      const accent =
        customColor?.button?.backgroundApp || THEME?.COLORS?.PRIMARY_900;
      // Barra indica o LADO em que o chip solto vai cair (semântica do splice:
      // vindo da esquerda cai DEPOIS do alvo; da direita, ANTES).
      const showLeftBar = isDropTarget && dragState.from > index;
      const showRightBar = isDropTarget && dragState.from < index;

      return (
        <div
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', String(index));
            setDragState({ from: index, over: null });
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDragState((s) => (s.over === index ? s : { ...s, over: index }));
          }}
          onDrop={(e) => {
            e.preventDefault();
            const from = Number(e.dataTransfer.getData('text/plain'));
            handleDrop(Number.isFinite(from) ? from : null, index);
          }}
          onDragEnd={() => setDragState({ from: null, over: null })}
          style={{
            display: 'flex',
            cursor: 'grab',
            opacity: isDragging ? 0.35 : 1,
            borderLeft: `3px solid ${showLeftBar ? accent : 'transparent'}`,
            borderRight: `3px solid ${showRightBar ? accent : 'transparent'}`,
            transition: 'opacity 120ms ease',
          }}
        >
          {chip}
        </div>
      );
    };

    return (
      <S.Container pointerEvents="box-none" compact={isSmallScreen}>
        <FlatList
          data={data}
          keyExtractor={(row, idx) =>
            row.type === 'clear'
              ? 'clear'
              : row.type === 'action'
                ? row.key
                : row?.item?.value ?? `item-${idx}`
          }
          horizontal
          showsHorizontalScrollIndicator={false}
          // Mesma sarjeta do header (`padding: 0.7rem` do HeaderContainer), em
          // qualquer largura: o primeiro chip alinha com o ícone de menu. Era
          // `wp(5)` — 5% da tela, que no desktop abria um vão de ~95px à
          // esquerda do primeiro filtro.
          contentContainerStyle={{
            paddingHorizontal: contentPaddingHorizontal ?? HEADER_GUTTER,
            alignItems: 'center',
          }}
          renderItem={renderRow}
          // `isSmallScreen` entra aqui senão os chips já montados não redesenham
          // ao girar o aparelho / redimensionar a janela.
          extraData={
            canDrag
              ? { selected, dragState, isSmallScreen }
              : `${selected}|${isSmallScreen}`
          }
        />
      </S.Container>
    );
  }
);

FilterBar.displayName = 'FilterBar';

export default FilterBar;
